import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  CreditCard, 
  Package, 
  FileText, 
  ChevronRight,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Zap
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MDQuickApprovalsWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [counts, setCounts] = useState({
    gtp_pending: 0,
    credit_pending: 0,
    stock_adjustments_pending: 0,
    credit_notes_pending: 0,
    total: 0
  });

  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchCounts = useCallback(async () => {
    try {
      // Fetch bottlenecks data which has all pending approvals
      const response = await fetch(`${API_URL}/api/ceo-dashboard/bottlenecks`, { headers });
      if (response.ok) {
        const data = await response.json();
        const gtpPending = data.pending_approvals?.gtp_approval || 0;
        const creditPending = data.pending_approvals?.credit_over_limit || 0;
        const stockAdjPending = data.pending_approvals?.stock_adjustments || 0;
        const creditNotesPending = data.pending_approvals?.credit_notes || 0;
        
        setCounts({
          gtp_pending: gtpPending,
          credit_pending: creditPending,
          stock_adjustments_pending: stockAdjPending,
          credit_notes_pending: creditNotesPending,
          total: gtpPending + creditPending + stockAdjPending + creditNotesPending
        });
      }
    } catch (error) {
      console.error('Failed to fetch approval counts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    // Refresh every 2 minutes
    const interval = setInterval(fetchCounts, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCounts();
  };

  const approvalItems = [
    {
      label: 'GTP Approvals',
      description: 'Good To Pay - Supplier Invoices',
      count: counts.gtp_pending,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700',
      badgeColor: 'bg-green-600',
      path: '/purchase-workflow',
      testId: 'md-quick-gtp'
    },
    {
      label: 'Credit Approvals',
      description: 'Over-limit Customer Orders',
      count: counts.credit_pending,
      icon: CreditCard,
      color: 'bg-blue-100 text-blue-700',
      badgeColor: 'bg-blue-600',
      path: '/sales-pipeline',
      testId: 'md-quick-credit'
    },
    {
      label: 'Stock Adjustments',
      description: 'Inventory Corrections',
      count: counts.stock_adjustments_pending,
      icon: Package,
      color: 'bg-amber-100 text-amber-700',
      badgeColor: 'bg-amber-600',
      path: '/inventory/stock-adjustments',
      testId: 'md-quick-stock-adj'
    },
    {
      label: 'Credit Notes',
      description: 'Customer Returns/Credits',
      count: counts.credit_notes_pending,
      icon: FileText,
      color: 'bg-purple-100 text-purple-700',
      badgeColor: 'bg-purple-600',
      path: '/inventory/credit-notes',
      testId: 'md-quick-credit-notes'
    }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="md-approvals-loading">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-red-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="md-quick-approvals-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            <div>
              <p className="font-bold text-lg">Quick Approvals</p>
              <p className="text-red-100 text-xs">Tap to review & approve</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {counts.total > 0 && (
              <Badge className="bg-white text-red-600 font-bold px-3 py-1">
                {counts.total} Pending
              </Badge>
            )}
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="md-approvals-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* No pending approvals message */}
      {counts.total === 0 && (
        <div className="p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">All caught up!</p>
          <p className="text-slate-400 text-sm">No pending approvals</p>
        </div>
      )}

      {/* Approval Items */}
      {counts.total > 0 && (
        <div className="divide-y">
          {approvalItems.filter(item => item.count > 0).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors active:bg-slate-100"
                data-testid={item.testId}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${item.badgeColor} text-white font-bold min-w-[28px] justify-center`}>
                    {item.count}
                  </Badge>
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* View All Link */}
      {counts.total > 0 && (
        <div className="p-3 bg-slate-50 border-t">
          <button
            onClick={() => navigate('/inventory')}
            className="w-full text-center text-sm text-red-600 font-medium hover:text-red-700"
            data-testid="md-approvals-view-all"
          >
            View Inventory Dashboard →
          </button>
        </div>
      )}

      {/* Urgent Alert */}
      {counts.total >= 5 && (
        <div className="p-3 bg-amber-50 border-t flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <p className="text-xs text-amber-700">
            <strong>{counts.total} items</strong> awaiting your approval
          </p>
        </div>
      )}
    </div>
  );
}
